import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * GoogleAnalyticsPageView — fires a GA4 `page_view` event on every React
 * Router navigation. Must be rendered inside `<BrowserRouter>` to access
 * `useLocation`.
 *
 * Why this exists:
 *   The gtag.js snippet in index.html fires ONE `gtag('config', ...)` call
 *   on the hard page load, which GA4 records as a single `page_view`. After
 *   that, every React Router `pushState` is invisible to GA — the browser
 *   doesn't reload, so the gtag snippet doesn't re-run. Without a component
 *   like this, GA4 dashboards see entry hits only: engagement, pages-per-
 *   session, and SPA funnel data all collapse. That matches the symptom
 *   "we're not getting data on users on the site".
 *
 * Parallels PostHogPageView; intentionally kept as a separate component so
 * the two analytics pipelines can be enabled/disabled independently.
 *
 * Guards:
 *   - No-op when `VITE_GA_MEASUREMENT_ID` is unset. Staging and preview
 *     builds either omit the variable or set it to a different stream so
 *     they don't pollute production analytics.
 *   - No-op when `window.gtag` is undefined. This covers three cases:
 *       1. Ad-blocker stripped the gtag.js load.
 *       2. Local dev without the GA script injected (some dev builds).
 *       3. The inline bootstrap <script> in index.html failed to run (CSP
 *          violation, parse error, etc.) — firing into the void would just
 *          TypeError; better to bail.
 *   - No-op during prerender. scripts/prerender.mjs sets
 *     `window.__IS_PRERENDERING__ = true` before app scripts run; without
 *     this guard every prerendered route in CI would emit a fake page_view
 *     tagged as 127.0.0.1, polluting the real analytics stream.
 *
 * Event shape:
 *   GA4's recommended way to log SPA navigations is an explicit `page_view`
 *   event with `page_path`, `page_location`, and `page_title`. We do NOT
 *   re-issue `gtag('config', ...)` per-route — that would reset session
 *   state on every navigation and confuse session attribution.
 */

// GA4 measurement ID — sourced from VITE_GA_MEASUREMENT_ID at build time.
// The same env var drives the inline bootstrap in frontend/index.html via
// Vite's HTML placeholder substitution, so the two stay in sync without a
// hardcoded duplicate. Empty string when unset (staging, preview, local) —
// the effect below early-returns, and the index.html bootstrap also bails,
// so no GA traffic is generated.
const GA_MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID ?? '') as string;

// Narrow the global gtag function type without adding a global augmentation
// file — the component is the only caller and the shape is trivial.
type GtagFn = (command: 'event', eventName: string, params: Record<string, unknown>) => void;

interface WindowWithGtag extends Window {
  gtag?: GtagFn;
  __IS_PRERENDERING__?: boolean;
}

export default function GoogleAnalyticsPageView(): null {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Skip when no GA stream is configured for this build. The inline
    // bootstrap in index.html also bails in this case, so window.gtag will
    // be undefined and the next guard would catch it too — this check is
    // here for clarity and to avoid the lookup cost.
    if (!GA_MEASUREMENT_ID) return;

    const w = window as WindowWithGtag;

    // Skip during prerender — scripts/prerender.mjs drives navigation
    // through Puppeteer and would otherwise emit synthetic page_views tagged
    // as the local static server's 127.0.0.1 origin.
    if (w.__IS_PRERENDERING__) return;

    // Guard against ad-blockers / CSP failures / local-dev misconfig. The
    // gtag function is installed synchronously by the inline bootstrap in
    // index.html, so if it's missing here, the snippet didn't run.
    if (typeof w.gtag !== 'function') return;

    // page_location should include the full URL (origin + path + search +
    // hash) so GA's URL reports match what the user actually sees in the
    // address bar. page_path is the pathname + search — the part that GA
    // groups by. page_title falls back to a blank string rather than the
    // undefined default so `document.title` at the moment of navigation is
    // what's logged, not stale title from a prior route.
    w.gtag('event', 'page_view', {
      page_path: `${location.pathname}${location.search}`,
      page_location: window.location.href,
      page_title: document.title,
      send_to: GA_MEASUREMENT_ID,
    });
    // location.hash intentionally excluded from deps: most routes don't use
    // it, and when they do (in-page anchors), re-firing page_view on every
    // scroll-to anchor is noise, not signal.
  }, [location.pathname, location.search]);

  return null;
}
